import { create } from 'zustand';

import { Actions, Overrides, Subjects } from '@cast/shared-ability-data-access';

type UserAbilityOverridesState = {
  userId: string | undefined;
  overrides: Overrides;
  setUserId: (userId: string) => void;
  override: (subject: Subjects, action: Actions, enabled: boolean) => void;
  clearOverride: (subject: Subjects, action?: Actions) => void;
  clearAll: () => void;
};

const version = '2';
const getStorageKey = (userId: string) =>
  `user-ability-overrides-${version}-${userId}`;

const loadOverridesFromStorage = (userId: string): Overrides => {
  try {
    const stored = localStorage.getItem(getStorageKey(userId));
    if (!stored) {
      return {};
    }
    return JSON.parse(stored);
  } catch {
    return {};
  }
};

const saveOverridesToStorage = (
  userId: string | undefined,
  overrides: Overrides
) => {
  if (!userId) return;
  try {
    localStorage.setItem(getStorageKey(userId), JSON.stringify(overrides));
  } catch {
    // Ignore storage errors
  }
};

export const useUserAbilityOverrides = create<UserAbilityOverridesState>()(
  (set, get) => ({
    userId: undefined,
    overrides: {},

    setUserId: (userId: string) => {
      const overrides = loadOverridesFromStorage(userId);
      set({ userId, overrides });
    },

    override: (subject, action, enabled) => {
      set((state) => {
        const newOverrides = {
          ...state.overrides,
          [subject]: {
            ...state.overrides[subject],
            [action]: enabled,
          },
        };
        saveOverridesToStorage(state.userId, newOverrides);
        return { overrides: newOverrides };
      });
    },

    clearOverride: (subject, action) => {
      set((state) => {
        let newOverrides: Overrides;
        if (action) {
          const subjectOverrides = { ...state.overrides[subject] };
          delete subjectOverrides[action];
          const hasRemainingActions = Object.keys(subjectOverrides).length > 0;
          newOverrides = {
            ...state.overrides,
            [subject]: hasRemainingActions ? subjectOverrides : undefined,
          };
        } else {
          newOverrides = { ...state.overrides };
          delete newOverrides[subject];
        }
        saveOverridesToStorage(state.userId, newOverrides);
        return { overrides: newOverrides };
      });
    },

    clearAll: () => {
      const { userId } = get();
      set({ overrides: {} });
      saveOverridesToStorage(userId, {});
    },
  })
);
